import { useEffect } from 'react'

import {
  Box,
  Form,
  Icon,
  Link,
  Radio,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import type { AttendanceEnumsPoolTypeEnum } from 'generated/resources/graphql'
import { isNumber } from 'lodash'

import Callout from 'modules/core/components/Callout'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import PayrollMappings from 'modules/attendance/components/CustomTimeRanges/Content/Table/Modals/SpecialHourWizard/CompensationMethod/PayrollMappings'
import { manageMapping } from 'modules/attendance/components/CustomTimeRanges/Content/Table/Modals/TimeMappingDropdown/utils/manageMappping'
import {
  ExtraHoursRulesData,
  ExtraHoursRulesDataField,
  validateMandatoryFields,
} from 'modules/attendance/lib/forms/utils/ExtraHourRulesWizardData'
import { getTrackingOrigin } from 'modules/attendance/lib/trackingUtils'
import { COMPENSATION_POOL_TYPE } from 'modules/attendance/lib/types'
import { PayrollConceptSelect } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'

const trackPoolTypeChange = (
  data: ExtraHoursRulesData,
  poolType: string,
  origin: string | undefined
) => {
  track(
    data.isDefault
      ? trackingEvents.timeTracking.baseRuleCompensationPressed
      : trackingEvents.timeTracking.customRuleCompensationPressed,
    {
      poolType,
      isEdit: data.isEdit,
      id: data.id,
      origin,
    }
  )
}

function getPayrollConceptIdByName<T extends { name?: string; id?: number }>(
  concepts: T[] | null | undefined,
  name: string
) {
  if (!concepts) return undefined
  const found = concepts.find((concept) => concept.name === name)
  return found?.id
}

const CompensationMethodStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<ExtraHoursRulesData>) => {
  const isPayrollOptionEnabled = data.hasCompensations || data.payrollEnabled
  const origin = getTrackingOrigin(data.employeeType)

  const handlePoolTypeChange = (poolType: AttendanceEnumsPoolTypeEnum) => {
    onDataChanged({ ...data, poolType })
    trackPoolTypeChange(data, poolType, origin)
  }

  const handleNext = async () => {
    const mandatoryFields: ExtraHoursRulesDataField[] = ['poolType']

    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    trackPoolTypeChange(data, data.poolType, origin)

    if (invalidFields.length) {
      return Promise.reject()
    }
  }
  const { navigation } = useNavigation()
  const route = navigation.payrollBundle.fullPath()

  const onPayrollConceptSelected = (payrollConceptId: unknown[] | unknown | null) => {
    if (isNaN(Number(payrollConceptId)) || !payrollConceptId || !isNumber(payrollConceptId)) {
      return
    }

    onDataChanged({ ...data, payrollConceptId })
  }

  const existingCompensationMapping = data.payrollMappingsCompensations?.find(
    (mapping) => mapping.reimbursableId === data.timeSettingsCustomTimeRangeCategoryId
  )

  const payrollConceptId =
    data.payrollConceptId ||
    existingCompensationMapping?.payrollConceptId ||
    getPayrollConceptIdByName(data.payrollConcepts, DEFAULT_RESULT_CONCEPT.es_overtime_extra_hours)

  useEffect(() => {
    if (!payrollConceptId) return
    onPayrollConceptSelected(payrollConceptId)
  }, [payrollConceptId])

  const payrollConceptsOptions = useCategorizedPayrollConcepts(data.payrollConcepts ?? [])

  useOnNext(handleNext)

  return (
    <Box flexDirection="row" gap="s48" hideScrollBar overflowY="auto" flexGrow>
      <Box gap="s24" width="s400">
        <Box gap="s8">
          <Text.Header2 weight="semibold">
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.title'
            )}
          </Text.Header2>
          <Text.Normal>
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.extra_hours_description'
            )}
          </Text.Normal>
        </Box>
        <Form padding="s0" notRounded>
          <Box gap="s16">
            <Radio
              name="timeoff"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="time-off" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {i18n.t('time_tracking.settings.time_blocks.categories.modals.time_off')}
                    </Text>
                  </Box>
                ),
                value: COMPENSATION_POOL_TYPE.TIMEOFF,
              }}
              onChange={() => handlePoolTypeChange(COMPENSATION_POOL_TYPE.TIMEOFF)}
              value={data.poolType}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.timeoff_description'
              )}
            />
            <Radio
              name="payroll"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="payroll" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {i18n.t('time_tracking.settings.time_blocks.categories.modals.payslip')}
                    </Text>
                  </Box>
                ),
                value: COMPENSATION_POOL_TYPE.VIRTUAL_PAYROLL,
                disabled: !isPayrollOptionEnabled,
              }}
              onChange={() => handlePoolTypeChange(COMPENSATION_POOL_TYPE.VIRTUAL_PAYROLL)}
              value={data.poolType}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_description'
              )}
            >
              {data.payrollEnabled && !data.hasCompensations && (
                <PayrollMappings
                  contractsTaxonomies={data.contractTaxonomies}
                  payrollMappingsTaxonomies={data.payrollMappingsTaxonomies}
                  formMappingsTaxonomies={data.formMappingsTaxonomies}
                  timeSettingsCategoryId={data.timeSettingsCustomTimeRangeCategoryId}
                  onChange={({ taxonomyId, legalEntityId, mappingId }) => {
                    //TODO: Remove this once the new compensation module is fully released
                    const formMappingsTaxonomies = manageMapping({
                      taxonomyId,
                      mappingId,
                      timeRangeCategoryId: data.timeSettingsCustomTimeRangeCategoryId,
                      legalEntityId,
                      formMappings: data.formMappingsTaxonomies,
                    })

                    track(trackingEvents.timeTracking.payrollTaxonomyMapped)

                    onDataChanged({ ...data, formMappingsTaxonomies })
                  }}
                />
              )}
              {data.hasCompensations && (
                <PayrollConceptSelect
                  payrollConcepts={payrollConceptsOptions}
                  value={payrollConceptId ? [payrollConceptId] : []}
                  onChange={onPayrollConceptSelected}
                />
              )}
            </Radio>
            <Radio
              name="balance"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="balance" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {i18n.t(
                        'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_title'
                      )}
                    </Text>
                  </Box>
                ),
                value: COMPENSATION_POOL_TYPE.BALANCE,
              }}
              onChange={() => handlePoolTypeChange(COMPENSATION_POOL_TYPE.BALANCE)}
              value={data.poolType}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_description'
              )}
            />
          </Box>
        </Form>
      </Box>
      <Box justifyContent="center" width="s360">
        {data.poolType === COMPENSATION_POOL_TYPE.VIRTUAL_PAYROLL &&
          !data.payrollEnabled &&
          !data.hasCompensations && (
            <Callout
              type="critical"
              title={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_title'
              )}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_description'
              )}
              link={
                <Link.Href href={route} target="_blank">
                  {i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_link'
                  )}
                </Link.Href>
              }
            />
          )}
      </Box>
    </Box>
  )
}

export const compensationMethodStep = (): WizardStep<ExtraHoursRulesData> => ({
  title: i18n.t(
    'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.title'
  ),
  nextText: i18n.t('common.save'),
  render: (props) => <CompensationMethodStep {...props} />,
  isCompleted: (data) => {
    const mandatoryFields: ExtraHoursRulesDataField[] = ['poolType']
    const { invalidFields } = validateMandatoryFields(mandatoryFields, data)
    return invalidFields.length === 0
  },
})
